/** Live presentation follows the current scene, including in-place rebuilds and iframe replacement. */
type Message = {type: string; payload?: Record<string, unknown>; [key: string]: unknown};
type Pending = {message: Message; encounterId: string; queuedAt: number};
const pending: Pending[] = [];
const readyListeners = new Set<() => void>();
const loadingDocuments = new WeakSet<Document>();
let mounted = false;

declare global { interface Window { __ftLocalEncounterSceneMounted?: boolean } }

export function currentFt3dFrame(): HTMLIFrameElement | null {
    return document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
}

/** An existing iframe owns the scene even while loading; only an explicit local mount uses this page. */
export function currentFt3dSceneWindow(): Window | null {
    const frame = currentFt3dFrame();
    return frame ? frame.contentWindow : window.__ftLocalEncounterSceneMounted === true ? window : null;
}

export function currentFt3dSceneDocument(): Document | null {
    try { return currentFt3dSceneWindow()?.document ?? null; } catch { return null; }
}

function activeEncounter(): string {
    return String((window as unknown as {ftGetActiveEncounterId?: () => string}).ftGetActiveEncounterId?.() ?? '').toUpperCase();
}

export function ft3dSceneReady(): boolean {
    try {
        const frame = currentFt3dFrame();
        const sceneDocument = frame ? frame.contentDocument : document;
        return !!sceneDocument && !loadingDocuments.has(sceneDocument)
            && sceneDocument.readyState === 'complete'
            && (currentFt3dSceneWindow() as (Window & {__ftZoneGenerationPreview?: {sceneReady?: boolean}}) | null)
                ?.__ftZoneGenerationPreview?.sceneReady === true;
    } catch { return false; }
}

function receipt(message: Message, status: 'queued' | 'posted'): void {
    document.dispatchEvent(new CustomEvent('ft:ft3d-presentation-delivery', {detail: {
        type: message.type, id: message.payload?.id ?? message.payload?.rollId ?? '', status,
    }}));
}

function post(message: Message, delayedMs = 0): void {
    let payload = message.payload;
    if (payload && delayedMs > 0) {
        // Audio may already have created this action's arrival clock. Loading must not expire its
        // queued visual before the receiver exists; retain the authored gesture/release interval.
        payload = {...payload};
        const timing = payload.presentationTiming as Record<string, number> | undefined;
        if (timing) payload.presentationTiming = Object.fromEntries(Object.entries(timing).map(([key, at]) => [key, at + delayedMs]));
        for (const key of ['releaseAt', 'impactAt']) if (typeof payload[key] === 'number') payload[key] += delayedMs;
    }
    currentFt3dSceneWindow()?.postMessage({...message, ...(payload ? {payload} : {})}, window.location.origin);
    receipt(message, 'posted');
}

function flush(): void {
    if (!ft3dSceneReady()) return;
    const encounterId = activeEncounter();
    for (const entry of pending.splice(0)) {
        if (encounterId && entry.encounterId && encounterId !== entry.encounterId) continue;
        post(entry.message, Math.max(0, Date.now() - entry.queuedAt));
    }
    for (const listener of readyListeners) listener();
}

function mount(): void {
    if (mounted) return;
    mounted = true;
    window.addEventListener('message', event => {
        if (event.origin !== window.location.origin || event.source !== currentFt3dSceneWindow()) return;
        if (event.data?.type === 'ft3d-scene-loading') {
            const frame = currentFt3dFrame();
            const sceneDocument = frame ? frame.contentDocument : document;
            if (sceneDocument) {
                if (event.data.payload?.loading === true) loadingDocuments.add(sceneDocument);
                else if (event.data.payload?.loading === false) loadingDocuments.delete(sceneDocument);
            }
        }
        if (event.data?.type === 'ft3d-scene-ready'
            || event.data?.type === 'ft3d-scene-loading' && event.data.payload?.loading === false) flush();
    });
    document.addEventListener('load', flush, true);
    new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node instanceof Element
            && (node.matches('[data-ft3d-encounter-frame]') || node.querySelector('[data-ft3d-encounter-frame]'))))) flush();
    }).observe(document.documentElement, {childList: true, subtree: true});
}

export function onFt3dSceneReady(listener: () => void): void {
    mount();
    readyListeners.add(listener);
}

export function postFt3dPresentation(message: Message): void {
    if (ft3dSceneReady()) { post(message); return; }
    mount();
    pending.push({message, encounterId: String(message.payload?.encounterId ?? activeEncounter()).toUpperCase(), queuedAt: Date.now()});
    receipt(message, 'queued');
}
